import { useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import { useLocation } from "react-router-dom";

import type { Client } from "../api/types";
import Notifications from "./Notifications";
import Sidebar from "./Sidebar";
import UserMenu from "./UserMenu";
import "./AppShell.css";

/** The rail's two widths. Kept equal to .sidebar and .sidebar--collapsed in
 *  Sidebar.css, which is where they are drawn; these are only for working out
 *  how wide the page will be once the rail has finished moving. */
const RAIL_OPEN = 300;
const RAIL_CLOSED = 72;
/** How long the rail's contents take to fade out before it moves, and how long
 *  it takes to move. Matched to the transitions in Sidebar.css. */
const FADE_OUT_MS = 80;
const SLIDE_MS = 220;

/** Figma: 1920 wide, sidebar 240, main column 1280, right rail 400.
 *  The rail is optional; screens without one let the main column run wide. */
export default function AppShell({
  clients,
  clientsFailed = false,
  onRetryClients,
  collapsed = false,
  onToggleCollapsed,
  topbar,
  rail,
  children,
}: {
  clients: Client[];
  /** The list could not be loaded, which is not the same as there being
   *  none. Passed through rather than handled here so the sidebar can say so
   *  where the list would have been. */
  clientsFailed?: boolean;
  onRetryClients?: () => void;
  collapsed?: boolean;
  onToggleCollapsed?: () => void;
  topbar?: ReactNode;
  rail?: ReactNode;
  children: ReactNode;
}) {
  const { pathname } = useLocation();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  /**
   * The rail opening and closing, done so it stays smooth.
   *
   * Animating the rail's width on its own made the page beside it lay itself
   * out again on every frame, and on the roles directory that costs about
   * 17ms, the whole of a frame: it stuttered, and the table shuffled while it
   * moved. Held at one width while the rail moves, the page only slides and
   * the same frame costs about 4ms.
   *
   * So, in order: the rail's contents fade out, the page is held at the wider
   * of its two widths, the rail slides, the page is let go, and the contents
   * fade back in already laid out for where the rail ended up. Nobody sees the
   * client names squeezed into a rail half their width, or icons hopping to
   * their new places, because both happen while the contents are faded out.
   *
   * shown is the layout the rail is drawn in, which lags collapsed by the
   * fade. Skipped for anyone who has asked for reduced motion, and below
   * 1024px, where the rail is a drawer that slides in on its own.
   */
  const [shown, setShown] = useState(collapsed);
  const [moving, setMoving] = useState(false);
  const shellRef = useRef<HTMLDivElement | null>(null);
  const bodyRef = useRef<HTMLDivElement | null>(null);

  // Runs on collapsed alone. Keyed on shown as well, the effect re-ran the
  // moment the layout switched part way through, and its cleanup cancelled the
  // rest of the sequence, leaving the contents faded out for good.
  const last = useRef(collapsed);
  useEffect(() => {
    if (last.current === collapsed) return;
    last.current = collapsed;
    const still =
      window.matchMedia("(prefers-reduced-motion: reduce)").matches ||
      window.matchMedia("(max-width: 1024px)").matches;
    if (still) {
      setShown(collapsed);
      setMoving(false);
      return;
    }

    const body = bodyRef.current;
    const shell = shellRef.current;
    const release = () => {
      if (body) {
        body.style.flex = "";
        body.style.width = "";
      }
    };
    let slide = 0;
    setMoving(true);
    const fade = window.setTimeout(() => {
      if (body && shell) {
        const now = body.getBoundingClientRect();
        const railNow = now.left - shell.getBoundingClientRect().left;
        const after = now.width + railNow - (collapsed ? RAIL_CLOSED : RAIL_OPEN);
        // The wider of the two. Narrower would leave a strip of empty page at
        // the right edge while the rail moves; wider is simply clipped by the
        // shell, which does not scroll.
        body.style.flex = "none";
        body.style.width = `${Math.max(now.width, after)}px`;
      }
      setShown(collapsed);
      slide = window.setTimeout(() => {
        release();
        setMoving(false);
      }, SLIDE_MS + 20);
    }, FADE_OUT_MS);

    // Clicked again part way through: drop this sequence and let the next one
    // start from wherever the rail has got to.
    return () => {
      window.clearTimeout(fade);
      window.clearTimeout(slide);
      release();
    };
  }, [collapsed]);

  useEffect(() => {
    setMobileNavOpen(false);
  }, [pathname]);

  return (
    <div
      ref={shellRef}
      className={[
        "shell",
        shown ? "shell--collapsed" : "",
        mobileNavOpen ? "shell--nav-open" : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <Sidebar
        clients={clients}
        clientsFailed={clientsFailed}
        onRetryClients={onRetryClients}
        collapsed={shown}
        moving={moving}
        onToggleCollapsed={onToggleCollapsed}
        mobileOpen={mobileNavOpen}
        onCloseMobile={() => setMobileNavOpen(false)}
      />
      <button
        type="button"
        className="shell__scrim"
        aria-label="Close navigation"
        aria-hidden={!mobileNavOpen}
        tabIndex={mobileNavOpen ? 0 : -1}
        onClick={() => setMobileNavOpen(false)}
      />
      <div className="shell__body" ref={bodyRef}>
        {topbar && (
          <header className="shell__topbar">
            <button
              type="button"
              className="shell__menu"
              aria-label="Open navigation"
              aria-expanded={mobileNavOpen}
              onClick={() => setMobileNavOpen(true)}
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                <path
                  d="M3.5 5.5h13M3.5 10h13M3.5 14.5h13"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                />
              </svg>
            </button>
            {topbar}
            {/* Account and alerts sit at the top right, which is where 1v1
                asked for them back. They are the signed-in person's own
                controls rather than the screen's, so they stay put across
                screens while the breadcrumb on the left changes. margin-left
                on the group holds them against the right edge whatever the
                breadcrumb's width. */}
            <div className="shell__account">
              <Notifications />
              <UserMenu />
            </div>
          </header>
        )}
        <div className={`shell__content ${rail ? "shell__content--railed" : ""}`}>
          <main className="shell__main">{children}</main>
          {rail && <aside className="shell__rail">{rail}</aside>}
        </div>
      </div>
    </div>
  );
}
